import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations as base, LANGUAGES } from "@/lib/translations";
import { translationsExtra } from "@/lib/translationsExtra";
import { translationsRewards } from "@/lib/translationsRewards";

// 2-level deep merge: extra overrides/adds section keys while preserving base.
function mergeTranslations(a, b) {
    const out = {};
    const langs = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const lang of langs) {
        const baseLang = a[lang] || {};
        const extraLang = b[lang] || {};
        const merged = { ...baseLang };
        for (const section of Object.keys(extraLang)) {
            merged[section] = { ...(baseLang[section] || {}), ...extraLang[section] };
        }
        out[lang] = merged;
    }
    return out;
}

const translations = mergeTranslations(
    mergeTranslations(base, translationsExtra),
    translationsRewards
);

const I18nContext = createContext(null);

const STORAGE_KEY = "sn-lang";

function detectDefaultLang() {
    if (typeof window === "undefined") return "it";
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && translations[saved]) return saved;
    const nav = (window.navigator.language || "it").slice(0, 2).toLowerCase();
    return translations[nav] ? nav : "it";
}

export function I18nProvider({ children }) {
    const [lang, setLang] = useState(detectDefaultLang);

    useEffect(() => {
        if (typeof window !== "undefined") {
            window.localStorage.setItem(STORAGE_KEY, lang);
            document.documentElement.setAttribute("lang", lang);
        }
    }, [lang]);

    const value = useMemo(() => {
        const dict = translations[lang] || translations.it;
        const t = (path) => {
            const keys = path.split(".");
            let cur = dict;
            for (const k of keys) {
                if (cur == null) return path;
                cur = cur[k];
            }
            if (cur == null) {
                // fallback to italian
                let fb = translations.it;
                for (const k of keys) {
                    if (fb == null) return path;
                    fb = fb[k];
                }
                return fb == null ? path : fb;
            }
            return cur;
        };
        return { lang, setLang, t, languages: LANGUAGES };
    }, [lang]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT() {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useT must be used inside I18nProvider");
    return ctx;
}
